<template>
  <div>
    <div class="columns">
      <div class="column">
        <div class="card">
          <span class="card-title">
            {{ $t("mp:delayed_transaction.summary.delayed_deals") }}
          </span>
          <span class="card-data">
            {{ getDetails.delayedDeals }}
            {{ $t("mp:pieces") }}
          </span>
        </div>
      </div>
      <div class="column">
        <div class="card">
          <span class="card-title">
            {{ $t("mp:delayed_transaction.summary.delayed_deals_amount") }}
          </span>
          <span class="card-data">
            {{
              formatMoney(
                country.languageIso,
                getDetails.currency,
                getDetails.delayedDealsAmount
              )
            }}
          </span>
        </div>
      </div>
      <div class="column">
        <div class="card">
          <span class="card-title">
            {{ $t("mp:delayed_transaction.summary.rate_delayed_deals") }}
          </span>
          <span class="card-data">
            {{
              localizedFormatter(country.languageIso).percent(
                getDetails.rateDelayedDeals
              )
            }}
          </span>
        </div>
      </div>
    </div>
    <div class="is-flex is-justify-content-flex-end mb-1">
      <a
        class="is-flex is-align-items-center is-ghost"
        @click="openStatusInfoModal"
      >
        <b-icon icon-pack="fa" icon="info-circle"></b-icon>
        {{ $t("mp:delayed_transaction_status.button.info") }}
      </a>
    </div>
    <b-modal v-model="isStatusInfoModalOpen">
      <div class="card is-flex is-flex-direction-column is-gap-16">
        <h2>{{ $t("mp:delayed_transaction.payee_status.modal.title") }}</h2>
        <div class="is-flex is-align-items-center is-gap-16">
          <span class="tag is-orange">
            {{ $t("mp:delayed_transaction.payee_status.deleted.tag") }}
          </span>
          <span>
            {{ $t("mp:delayed_transaction.payee_status.deleted.description") }}
          </span>
        </div>
        <div class="is-flex is-align-items-center is-gap-16">
          <span class="tag is-yellow">
            {{ $t("mp:delayed_transaction.payee_status.pending.tag") }}
          </span>
          <span>
            {{ $t("mp:delayed_transaction.payee_status.pending.description") }}
          </span>
        </div>
        <div class="is-flex is-align-items-center is-gap-16">
          <span class="tag is-blue">
            {{ $t("mp:delayed_transaction.payee_status.in_progress.tag") }}
          </span>
          <span>
            {{
              $t("mp:delayed_transaction.payee_status.in_progress.description")
            }}
          </span>
        </div>
        <div class="is-flex is-align-items-center is-gap-16">
          <span class="tag is-gray">
            {{ $t("mp:delayed_transaction.payee_status.closed.tag") }}
          </span>
          <span>
            {{ $t("mp:delayed_transaction.payee_status.closed.description") }}
          </span>
        </div>
        <div class="is-flex is-align-items-center is-gap-16">
          <span class="tag is-blue">
            {{ $t("mp:delayed_transaction.payee_status.in_progress_todo.tag") }}
          </span>
          <span>
            {{
              $t(
                "mp:delayed_transaction.payee_status.in_progress_todo.description"
              )
            }}
          </span>
        </div>
      </div>
    </b-modal>
    <div class="card">
      <Bar
        class="chart-container"
        :chartOptions="chartOptions"
        :chartData="chartData"
      />
    </div>
    <h1>{{ $t("mp:delayed_transaction.title") }}</h1>
    <FieldListing
      :data="getList"
      :columns="tableColumns"
      :search-fields="searchFields"
      :searchable="true"
      :paginated="false"
      default-sort="created"
      default-sort-direction="desc"
      store-fetch-command="payee/fetchList"
      store-get-command="payee/getList"
    ></FieldListing>
  </div>
</template>

<script>
import "chart.js/auto";
import { Bar } from "vue-chartjs";
import { mapActions, mapGetters } from "vuex";
import { localizedFormatter } from "../utils/util";
import FieldListing from "../components/FieldListing.vue";
import { SEARCH_FIELD_TYPES } from "../utils/const";

export default {
  name: "DelayedTransactions",
  components: {
    FieldListing,
    Bar,
  },
  data() {
    return {
      isStatusInfoModalOpen: false,
      chartData: {
        labels: [],
        datasets: [
          {
            label: "data",
            data: [],
            backgroundColor: "#0BDDFA",
            hoverOffset: 4,
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              boxWidth: 0,
              boxHeight: 0,
            },
          },
        },
        scales: {
          y: {
            ticks: {
              stepSize: 1,
            },
          },
        },
      },
      searchFields: [
        {
          name: "mp:delayed_transaction.table.deal_id.label",
          identifier: "dealId",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.created.label",
          identifier: "created",
          type: SEARCH_FIELD_TYPES.DATETIME,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.first_name.label",
          identifier: "firstName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.Last_name.label",
          identifier: "lastName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.total_amount.label",
          identifier: "totalAmount",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.late_amount.label",
          identifier: "lateAmount",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.delayed_days.label",
          identifier: "delayedDays",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.order_id.label",
          identifier: "orderId",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: true,
        },
        {
          name: "mp:delayed_transaction.table.deal_status.label",
          identifier: "dealStatus",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
      ],
      tableColumns: [
        {
          field: "dealId",
          label: "mp:delayed_transaction.table.deal_id.label",
          format: (row) => row.dealId,
        },
        {
          field: "created",
          label: "mp:delayed_transaction.table.created.label",
          format: (row) =>
            localizedFormatter(this.country.languageIso).date(row.created),
          defaultValue: `${
            new Date(new Date().setMonth(new Date().getMonth() - 6))
              .toISOString()
              .split("T")[0]
          } 00:00:00`,
        },
        {
          field: "firstName",
          label: "mp:delayed_transaction.table.first_name.label",
          format: (row) => row.firstName,
        },
        {
          field: "lastName",
          label: "mp:delayed_transaction.table.last_name.label",
          format: (row) => row.lastName,
        },
        {
          field: "totalAmount",
          label: "mp:delayed_transaction.table.total_amount.label",
          format: (row) => {
            if (row.totalAmount && row.currency) {
              return this.formatMoney(
                this.country.languageIso,
                row.currency,
                row.totalAmount
              );
            } else if (row.totalAmount) {
              return row.totalAmount;
            } else {
              return "-";
            }
          },
        },
        {
          field: "lateAmount",
          label: "mp:delayed_transaction.table.late_amount.label",
          format: (row) => {
            if (row.lateAmount && row.currency) {
              return this.formatMoney(
                this.country.languageIso,
                row.currency,
                row.lateAmount
              );
            } else if (row.lateAmount) {
              return row.lateAmount;
            } else {
              return "-";
            }
          },
        },
        {
          field: "delayedDays",
          label: "mp:delayed_transaction.table.delayed_days.label",
          format: (row) => row.delayedDays,
        },
        {
          field: "orderId",
          label: "mp:delayed_transaction.table.order_id.label",
          format: (row) => row.orderId,
        },
        {
          field: "payeeStatus",
          label: "mp:delayed_transaction.table.payee_status.label",
          vhtml: true,
          format: (row) => {
            if (row.payeeStatusCode === "pending") {
              return `<span class="tag is-yellow">${row.payeeStatus}</span>`;
            } else if (row.payeeStatusCode === "deleted") {
              return `<span class="tag is-orange">${row.payeeStatus}</span>`;
            } else if (
              row.payeeStatusCode === "in_progress" ||
              row.payeeStatusCode === "in_progress_todo"
            ) {
              return `<span class="tag is-blue">${row.payeeStatus}</span>`;
            } else if (row.payeeStatusCode === "closed") {
              return `<span class="tag is-gray">${row.payeeStatus}</span>`;
            } else {
              return "-";
            }
          },
        },
      ],
    };
  },
  methods: {
    localizedFormatter,
    ...mapActions({
      fetchChart: "payee/fetchChart",
      fetchDetails: "payee/fetchDetails",
    }),
    formatMoney(locale, currency, amount) {
      console.log(locale, currency, amount);
      if (!locale || !currency) {
        return amount;
      }
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
        trailingZeroDisplay: "stripIfInteger",
      }).format(amount);
    },
    openStatusInfoModal() {
      this.isStatusInfoModalOpen = true;
    },
  },
  computed: {
    ...mapGetters({
      getChart: "payee/getChart",
      getList: "payee/getList",
      getDetails: "payee/getDetails",
      country: "i18n/country",
    }),
  },
  async created() {
    this.fetchDetails();
    await this.fetchChart();
    this.chartData.labels = Object.keys(this.getChart);
    this.chartData.datasets[0].data = Object.values(this.getChart);
  },
};

/** Used tokens
 * mp:delayed_transaction.title
 * mp:delayed_transaction.summary.delayed_deals
 * mp:delayed_transaction.summary.delayed_deals_amount
 * mp:delayed_transaction.summary.rate_delayed_deals
 */
</script>

<style scoped lang="scss">
.card {
  border-radius: 8px;
  box-shadow: none;
  border: 1px solid #e0e5eb;
  background: white;
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .card-title {
    color: #677d99;
    font-size: 14px;
  }

  .card-data {
    padding: 0;
    font-size: 28px;
    font-weight: bold;
  }
}

.chart-container {
  height: 15rem;
}
</style>

<style lang="scss">
.table span.tag {
  margin: -16px 0px;
}

span.tag {
  // padding: 0.75em 0.75em !important;
  border-radius: 4px !important;

  &.is-yellow {
    background-color: #f3e432 !important;
    color: black !important;
  }

  &.is-orange {
    background-color: #e85e21 !important;
    color: white !important;
  }

  &.is-blue {
    background-color: #1072fc !important;
    color: white !important;
  }

  &.is.gray {
    background-color: #d9d0c9 !important;
    color: black !important;
  }
}

.is-gap-16 {
  gap: 16px;
}
</style>
